import { useCallback, useEffect, useRef, useState } from 'react'

interface PopupSize {
  width: number
  height: number
}

interface OAuthPopupOptions<TAuthData> {
  authUrl: string
  popupName: string
  successMessageType: string
  acknowledgmentMessageType: string
  extractAuthData: (eventData: any) => TAuthData | null
  stateStorage?: {
    key: string
    value: string
  }
  timeoutMs?: number
  popupSize?: PopupSize
}

const DEFAULT_POPUP_SIZE: PopupSize = {
  width: 600,
  height: 700
}

const DEFAULT_TIMEOUT_MS = 120000

export function useOAuthPopupFlow() {
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const isAuthenticatingRef = useRef(false)
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [])

  const runOAuthPopup = useCallback(
    <TAuthData,>(options: OAuthPopupOptions<TAuthData>): Promise<TAuthData> => {
      if (isAuthenticatingRef.current) {
        return Promise.reject(new Error('Authentication is already in progress'))
      }
      isAuthenticatingRef.current = true

      const popupSize = options.popupSize || DEFAULT_POPUP_SIZE
      const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS

      return new Promise<TAuthData>((resolve, reject) => {
        setIsAuthenticating(true)

        if (options.stateStorage) {
          sessionStorage.setItem(options.stateStorage.key, options.stateStorage.value)
        }

        const left = window.screen.width / 2 - popupSize.width / 2
        const top = window.screen.height / 2 - popupSize.height / 2
        const authWindow = window.open(
          options.authUrl,
          options.popupName,
          `width=${popupSize.width},height=${popupSize.height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        )

        if (!authWindow || authWindow.closed) {
          isAuthenticatingRef.current = false
          setIsAuthenticating(false)
          reject(new Error('Popup blocked! Please allow popups for this site and try again.'))
          return
        }

        let settled = false
        let timeoutId: number | undefined
        let closePollId: number | undefined

        const cleanup = () => {
          if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId)
          }
          if (closePollId !== undefined) {
            window.clearInterval(closePollId)
          }
          window.removeEventListener('message', handleMessage)
          cleanupRef.current = null
        }

        const finish = (handler: () => void) => {
          if (settled) {
            return
          }
          settled = true
          cleanup()
          isAuthenticatingRef.current = false
          setIsAuthenticating(false)
          handler()
        }

        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return
          }
          if (event.data?.type !== options.successMessageType) {
            return
          }

          const authData = options.extractAuthData(event.data)
          if (!authData) {
            finish(() => reject(new Error('Authentication failed: invalid response payload')))
            return
          }

          if (!authWindow.closed) {
            authWindow.postMessage({ type: options.acknowledgmentMessageType }, window.location.origin)
          }

          finish(() => resolve(authData))
        }

        timeoutId = window.setTimeout(() => {
          finish(() => reject(new Error('Authentication timed out')))
        }, timeoutMs)

        closePollId = window.setInterval(() => {
          if (authWindow.closed) {
            finish(() => reject(new Error('Authentication cancelled: popup was closed')))
          }
        }, 500)

        cleanupRef.current = cleanup
        window.addEventListener('message', handleMessage)
      })
    },
    []
  )

  return { runOAuthPopup, isAuthenticating }
}
